import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";
import { threadLastActivityAt } from "@cleo/client-runtime/state/thread-settled";
import type { WorldRoomDirectory, WorldRoomProjection } from "@cleo/contracts";

import { formatRelativeRailTime } from "./RoomRailSection.logic";

/**
 * The Room Rail's HOVER CARD (RM-CAN-10, dated revision 2026-09-15; the
 * principal: "show the full name of the room, or session, including time
 * created and time of the last message, the last message sent by user").
 *
 * The rail's width is its ceiling — a row can only truncate — so the card
 * floats beside the rail and says what the row cannot. This module is the
 * pure fold from the World's rows and the Desk's execution shells to the
 * card's facts; the component renders them and nothing else.
 */
export type RailHoverKind = "room" | "session" | "workroom";

export interface RailHoverFacts {
  readonly kind: RailHoverKind;
  /** The full label, never truncated. */
  readonly title: string;
  /** One quiet line under the title: room, standing, counts. */
  readonly meta: string | null;
  /** The earliest execution's birth, or null before the first thread. */
  readonly createdAt: string | null;
  /** The latest activity across the executions (a user or agent message). */
  readonly lastMessageAt: string | null;
  /** The opening characters of the person's latest message, if the shell carries them. */
  readonly lastUserMessage: string | null;
  /** A gesture the row hides (right-click, ⋮); null when the row has none. */
  readonly hint: string | null;
}

/** The card shows this many characters of the last user message. */
export const RAIL_HOVER_PREVIEW_CHARS = 140;

type MetaPart = string | null | undefined | false;

export function joinRailHoverMeta(parts: ReadonlyArray<MetaPart>): string | null {
  const words = parts.filter((part): part is string => typeof part === "string" && part.length > 0);
  return words.length === 0 ? null : words.join(" · ");
}

/** Collapse whitespace and cut at a word boundary; null when nothing is left to show. */
export function railHoverPreviewText(
  raw: string | null | undefined,
  max = RAIL_HOVER_PREVIEW_CHARS,
): string | null {
  if (typeof raw !== "string") return null;
  const collapsed = raw.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  const points = Array.from(collapsed);
  if (points.length <= max) return collapsed;
  const cut = points.slice(0, max).join("");
  const boundary = cut.lastIndexOf(" ");
  return `${boundary > max * 0.6 ? cut.slice(0, boundary) : cut}…`;
}

export interface RailHoverTime {
  readonly dateTime: string;
  /** "Sep 7, 6:58 PM" (with the year once it differs from today's). */
  readonly absolute: string;
  /** "3h ago", "just now". */
  readonly relative: string;
}

export function formatRailHoverTime(iso: string | null, nowMs: number): RailHoverTime | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const date = new Date(ms);
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  const absolute = date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
  const short = formatRelativeRailTime(nowMs - ms);
  return { dateTime: iso, absolute, relative: short === "now" ? "just now" : `${short} ago` };
}

const parse = (iso: string | null | undefined): number =>
  typeof iso === "string" ? Date.parse(iso) : Number.NaN;

/** Times across a set of executions: first birth, latest activity, the latest user message's preview. */
export function executionHoverTimes(
  executions: ReadonlyArray<EnvironmentThreadShell>,
): Pick<RailHoverFacts, "createdAt" | "lastMessageAt" | "lastUserMessage"> {
  let createdAt: string | null = null;
  let lastMessageAt: string | null = null;
  let latestUser: EnvironmentThreadShell | null = null;
  for (const shell of executions) {
    if (
      Number.isFinite(parse(shell.createdAt)) &&
      (createdAt === null || parse(shell.createdAt) < parse(createdAt))
    )
      createdAt = shell.createdAt;
    const activity = threadLastActivityAt(shell);
    if (activity !== null && (lastMessageAt === null || parse(activity) > parse(lastMessageAt)))
      lastMessageAt = activity;
    if (
      shell.latestUserMessageAt !== null &&
      (latestUser === null ||
        parse(shell.latestUserMessageAt) > parse(latestUser.latestUserMessageAt))
    )
      latestUser = shell;
  }
  return {
    createdAt,
    lastMessageAt,
    lastUserMessage: railHoverPreviewText(latestUser?.latestUserMessagePreview),
  };
}

export function sessionHoverFacts(input: {
  readonly label: string;
  readonly meta: ReadonlyArray<MetaPart>;
  readonly executions: ReadonlyArray<EnvironmentThreadShell>;
}): RailHoverFacts {
  return {
    kind: "session",
    title: input.label,
    meta: joinRailHoverMeta(input.meta),
    ...executionHoverTimes(input.executions),
    hint: null,
  };
}

export function roomHoverFacts(input: {
  readonly label: string;
  readonly meta: ReadonlyArray<MetaPart>;
  readonly executions: ReadonlyArray<EnvironmentThreadShell>;
}): RailHoverFacts {
  return {
    kind: "room",
    title: input.label,
    meta: joinRailHoverMeta(input.meta),
    ...executionHoverTimes(input.executions),
    hint: null,
  };
}

/** `roomStatusDescription` opens with the label; the card already wears it as the title. */
export function stripRoomLabel(description: string, label: string): string {
  const prefix = `${label} · `;
  return description.startsWith(prefix) ? description.slice(prefix.length) : description;
}

/** The dock chip's card: the room's sessions and their times. Plus and ⋮ show on
 * hover, so the card teaches no gesture. */
export function workroomHoverFacts(input: {
  readonly title: string;
  readonly room: WorldRoomProjection | undefined;
  readonly index: WorldRoomDirectory;
  readonly executionsBySession: ReadonlyMap<string, ReadonlyArray<EnvironmentThreadShell>>;
}): RailHoverFacts {
  if (input.room === undefined) {
    return {
      kind: "workroom",
      title: input.title,
      meta: "No sessions yet",
      createdAt: null,
      lastMessageAt: null,
      lastUserMessage: null,
      hint: null,
    };
  }
  const roomId = input.room.roomId;
  const sessions = input.index.sessions.filter((session) => session.roomId === roomId);
  const open = sessions.filter((session) => session.lifecycle !== "retired").length;
  const retired = sessions.length - open;
  const executions = sessions.flatMap(
    (session) => input.executionsBySession.get(session.sessionId) ?? [],
  );
  return {
    kind: "workroom",
    title: input.title,
    meta: joinRailHoverMeta([
      open === 0 ? "No open sessions" : `${open} ${open === 1 ? "session" : "sessions"}`,
      retired > 0 ? `${retired} retired` : null,
    ]),
    ...executionHoverTimes(executions),
    hint: null,
  };
}
